import { useCallback, useEffect, useMemo, useState } from "react";

import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { downloadAuthed } from "../utils/download";
import { SortableTh, sortRows, useSort } from "../components/SortableTh";

/**
 * PSA → Quotes. The step where a deal is actually won, which the product had
 * no model for at all: opportunities existed but nothing produced a priced
 * proposal or turned one into money.
 *
 * The lifecycle is the screen. Which buttons exist depends on status, because
 * the server refuses the rest anyway and offering a disabled "Accept" on an
 * expired quote just invites a support call:
 *
 *   draft    → send, edit, delete
 *   sent     → accept, decline, edit, PDF
 *   accepted → PDF, convert to invoice. NOTHING editable — it is the record
 *              of what the customer agreed to.
 *   declined / expired → reopen (back to draft), delete
 */

type Line = {
  description: string;
  quantity_thousandths: number;
  unit_label: string;
  unit_rate_cents: number;
  total_cents?: number;
  is_recurring: boolean;
};

type Quote = {
  id: number;
  customer_id: number | null;
  prospect_name: string | null;
  quote_number: string | null;
  status: string;
  title: string;
  issue_date: string | null;
  valid_until: string | null;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  is_expired: boolean;
  recurring_monthly_cents: number;
  accepted_by_name: string | null;
  accepted_at: string | null;
  decline_reason: string | null;
  converted_invoice_id: number | null;
  lines?: Line[];
};

type Customer = { id: number; name: string };

const money = (c: number) =>
  ((c || 0) / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });

const STATUS_STYLE: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300",
  sent: "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  accepted: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  declined: "bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  expired: "bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
};

const blankLine = (): Line => ({
  description: "",
  quantity_thousandths: 1000,
  unit_label: "each",
  unit_rate_cents: 0,
  is_recurring: false,
});

export default function PSAQuotes() {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [open, setOpen] = useState<Quote | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const sort = useSort("created", "desc");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Quotes",
    useMemo(
      () =>
        quotes.map((q: any) => ({
          number: q.quote_number ?? q.id,
          customer: q.customer_name ?? null,
          status: q.status,
          total: (q.total_cents ?? 0) / 100,
          created: q.created_at ?? null,
          expires: q.expires_at ?? null,
        })),
      [quotes],
    ),
    undefined,
  );

  // Draft being composed or edited.
  const [form, setForm] = useState<{
    customer_id: number | "";
    prospect_name: string;
    title: string;
    valid_until: string;
    customer_message: string;
    terms: string;
    lines: Line[];
  }>({
    customer_id: "",
    prospect_name: "",
    title: "",
    valid_until: "",
    customer_message: "",
    terms: "",
    lines: [blankLine()],
  });

  const load = useCallback(async () => {
    const r = await api.get("/api/v1/psa/quotes");
    setQuotes(r.data || []);
  }, []);

  useEffect(() => {
    load().catch(() => setErr("Could not load quotes."));
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers(r.data || []))
      .catch(() => setCustomers([]));
  }, [load]);

  const flash = (m: string) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 3000);
  };

  const act = async (q: Quote, verb: string, body?: any) => {
    setErr("");
    setBusy(`${q.id}-${verb}`);
    try {
      const r = await api.post(`/api/v1/psa/quotes/${q.id}/${verb}`, body ?? {});
      await load();
      if (verb === "convert-to-invoice") {
        flash(`Draft invoice #${r.data.invoice_id} created — review it before sending.`);
      } else {
        flash(`Quote ${verb === "reopen" ? "reopened" : verb + "ed"}`);
      }
      if (open?.id === q.id) setOpen(null);
    } catch (e: any) {
      // The server's message names the rule that was hit; it is the useful one.
      setErr(e?.response?.data?.detail || `Could not ${verb} this quote.`);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (q: Quote) => {
    if (!window.confirm(`Delete quote ${q.quote_number || q.title}?`)) return;
    setErr("");
    try {
      await api.delete(`/api/v1/psa/quotes/${q.id}`);
      await load();
      flash("Quote deleted");
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not delete this quote.");
    }
  };

  const startCreate = () => {
    setForm({
      customer_id: "",
      prospect_name: "",
      title: "",
      valid_until: "",
      customer_message: "",
      terms: "",
      lines: [blankLine()],
    });
    setCreating(true);
  };

  const startEdit = async (q: Quote) => {
    const full = (await api.get(`/api/v1/psa/quotes/${q.id}`)).data as Quote;
    setForm({
      customer_id: full.customer_id ?? "",
      prospect_name: full.prospect_name || "",
      title: full.title,
      valid_until: full.valid_until || "",
      customer_message: (full as any).customer_message || "",
      terms: (full as any).terms || "",
      lines: (full.lines || []).map((l) => ({ ...l })),
    });
    setOpen(full);
    setCreating(true);
  };

  const save = async () => {
    setErr("");
    setBusy("save");
    const body = {
      customer_id: form.customer_id || null,
      prospect_name: form.prospect_name.trim() || null,
      title: form.title.trim(),
      valid_until: form.valid_until || null,
      customer_message: form.customer_message.trim() || null,
      terms: form.terms.trim() || null,
      lines: form.lines
        .filter((l) => l.description.trim())
        .map((l) => ({
          description: l.description.trim(),
          quantity_thousandths: l.quantity_thousandths,
          unit_label: l.unit_label || "each",
          unit_rate_cents: l.unit_rate_cents,
          is_recurring: l.is_recurring,
        })),
    };
    try {
      if (open) {
        await api.patch(`/api/v1/psa/quotes/${open.id}`, body);
      } else {
        await api.post("/api/v1/psa/quotes", body);
      }
      await load();
      setCreating(false);
      setOpen(null);
      flash("Saved");
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not save this quote.");
    } finally {
      setBusy(null);
    }
  };

  const cName = (q: Quote) =>
    q.customer_id
      ? customers.find((c) => c.id === q.customer_id)?.name || `#${q.customer_id}`
      : q.prospect_name
        ? `${q.prospect_name} (prospect)`
        : "—";

  // Running total while composing, so the figure is visible before saving.
  const draftSubtotal = form.lines.reduce(
    (n, l) => n + Math.round((l.quantity_thousandths * l.unit_rate_cents) / 1000),
    0,
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Quotes</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Priced proposals. An accepted quote converts to a draft invoice with the figures the
            customer agreed to.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="bg-brand text-white text-sm px-4 py-2 rounded-lg hover:opacity-90"
        >
          New quote
        </button>
      </div>

      {err && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm px-4 py-2 rounded-lg">
          {err}
        </div>
      )}
      {msg && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm px-4 py-2 rounded-lg">
          {msg}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <table className="w-full text-sm md:min-w-[880px]">
          <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
            <tr>
              <SortableTh label="Quote #" sortKey="quote_number" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Customer" sortKey="customer" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Title" sortKey="title" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Status" sortKey="status" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Valid until" sortKey="valid_until" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Total" sortKey="total_cents" sort={sort.sort} onSort={sort.toggle} align="right" />
              <th className="text-left px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortRows(quotes, sort.sort, (q, key) => {
              switch (key) {
                case "quote_number":
                  return (q.quote_number || "").toLowerCase();
                case "customer":
                  return cName(q).toLowerCase();
                case "status":
                  return q.status;
                case "valid_until":
                  return q.valid_until ? Date.parse(q.valid_until) : 0;
                case "total_cents":
                  return q.total_cents;
                case "title":
                  return (q.title || "").toLowerCase();
                default:
                  return q.id;
              }
            }).map((q) => (
              <tr key={q.id} className="text-gray-900 dark:text-gray-100">
                <td className="px-3 py-2 font-mono text-xs">{q.quote_number || "draft"}</td>
                <td className="px-3 py-2 text-xs">{cName(q)}</td>
                <td className="px-3 py-2">
                  {q.title}
                  {q.recurring_monthly_cents > 0 && (
                    <span className="ml-2 text-[11px] text-gray-500 dark:text-gray-400">
                      {money(q.recurring_monthly_cents)}/mo recurring
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded ${
                      STATUS_STYLE[q.status] || STATUS_STYLE.draft
                    }`}
                  >
                    {q.status}
                  </span>
                  {/* Derived server-side, so a quote whose date passed an hour
                      ago does not sit here looking live. */}
                  {q.is_expired && q.status !== "expired" && (
                    <span className="ml-1 text-[11px] text-amber-700 dark:text-amber-400">
                      past validity
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{q.valid_until || "no expiry"}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{money(q.total_cents)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {q.status === "draft" && (
                    <>
                      <button
                        onClick={() => startEdit(q)}
                        className="text-xs text-brand dark:text-brand-light hover:underline mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => act(q, "send")}
                        disabled={busy === `${q.id}-send`}
                        className="text-xs text-brand dark:text-brand-light hover:underline mr-2 disabled:opacity-50"
                      >
                        {busy === `${q.id}-send` ? "…" : "Send"}
                      </button>
                    </>
                  )}
                  {q.status === "sent" && (
                    <>
                      <button
                        onClick={() => {
                          const who = window.prompt("Who accepted it? (optional)") ?? "";
                          act(q, "accept", { accepted_by_name: who });
                        }}
                        disabled={busy === `${q.id}-accept`}
                        className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline mr-2 disabled:opacity-50"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => {
                          const why = window.prompt("Reason (optional)") ?? "";
                          act(q, "decline", { reason: why });
                        }}
                        className="text-xs text-red-600 dark:text-red-400 hover:underline mr-2"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => startEdit(q)}
                        className="text-xs text-brand dark:text-brand-light hover:underline mr-2"
                      >
                        Edit
                      </button>
                    </>
                  )}
                  {q.status === "accepted" && !q.converted_invoice_id && (
                    <button
                      onClick={() => act(q, "convert-to-invoice")}
                      disabled={busy === `${q.id}-convert-to-invoice`}
                      className="text-xs text-brand dark:text-brand-light hover:underline mr-2 disabled:opacity-50"
                    >
                      {busy === `${q.id}-convert-to-invoice` ? "…" : "Convert to invoice"}
                    </button>
                  )}
                  {q.converted_invoice_id && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                      invoice #{q.converted_invoice_id}
                    </span>
                  )}
                  {(q.status === "declined" || q.status === "expired") && (
                    <button
                      onClick={() => act(q, "reopen")}
                      className="text-xs text-brand dark:text-brand-light hover:underline mr-2"
                    >
                      Reopen
                    </button>
                  )}
                  {/* Not an href: a navigation sends no bearer token, so this
                      showed {"detail":"Not authenticated"} rather than a quote. */}
                  <button
                    onClick={() =>
                      downloadAuthed(
                        `/api/v1/psa/quotes/${q.id}/pdf`,
                        `quote-${q.quote_number || q.id}.pdf`,
                        "application/pdf",
                      )
                    }
                    className="text-xs text-brand dark:text-brand-light hover:underline mr-2"
                  >
                    PDF
                  </button>
                  {q.status !== "accepted" && (
                    <button
                      onClick={() => remove(q)}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {quotes.length === 0 && (
              <tr>
                <td
                  className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 italic text-xs"
                  colSpan={7}
                >
                  No quotes yet — create one to price up a piece of work.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-40 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl my-8">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {open ? `Edit ${open.quote_number || "draft"}` : "New quote"}
              </h2>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  Customer
                  <select
                    value={form.customer_id}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        customer_id: e.target.value ? Number(e.target.value) : "",
                      })
                    }
                    className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 dark:text-gray-100"
                  >
                    <option value="">— not a customer yet —</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  Prospect name (if not a customer)
                  <input
                    value={form.prospect_name}
                    onChange={(e) => setForm({ ...form, prospect_name: e.target.value })}
                    disabled={!!form.customer_id}
                    className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm dark:bg-gray-900 dark:text-gray-100 disabled:opacity-50"
                  />
                </label>
                <label className="text-xs text-gray-500 dark:text-gray-400 sm:col-span-2">
                  Title
                  <input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="What is being quoted"
                    className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  Valid until
                  <input
                    type="date"
                    value={form.valid_until}
                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                    className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm dark:bg-gray-900 dark:text-gray-100"
                  />
                  <span className="block mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                    Leave blank and the price never expires.
                  </span>
                </label>
              </div>

              {/* Lines */}
              <div>
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Lines
                </div>
                <div className="space-y-2">
                  {form.lines.map((l, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center rounded-lg border border-gray-200 dark:border-gray-700 p-2"
                    >
                      <input
                        value={l.description}
                        placeholder="Description"
                        onChange={(e) => {
                          const lines = [...form.lines];
                          lines[i] = { ...l, description: e.target.value };
                          setForm({ ...form, lines });
                        }}
                        className="sm:col-span-5 border dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm dark:bg-gray-900 dark:text-gray-100"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={l.quantity_thousandths / 1000}
                        onChange={(e) => {
                          const lines = [...form.lines];
                          lines[i] = {
                            ...l,
                            quantity_thousandths: Math.round(Number(e.target.value) * 1000),
                          };
                          setForm({ ...form, lines });
                        }}
                        className="sm:col-span-2 border dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm dark:bg-gray-900 dark:text-gray-100"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={l.unit_rate_cents / 100}
                        onChange={(e) => {
                          const lines = [...form.lines];
                          lines[i] = {
                            ...l,
                            unit_rate_cents: Math.round(Number(e.target.value) * 100),
                          };
                          setForm({ ...form, lines });
                        }}
                        className="sm:col-span-2 border dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm dark:bg-gray-900 dark:text-gray-100"
                      />
                      <label className="sm:col-span-2 flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                        <input
                          type="checkbox"
                          checked={l.is_recurring}
                          onChange={(e) => {
                            const lines = [...form.lines];
                            lines[i] = { ...l, is_recurring: e.target.checked };
                            setForm({ ...form, lines });
                          }}
                        />
                        monthly
                      </label>
                      <button
                        onClick={() =>
                          setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) })
                        }
                        className="sm:col-span-1 text-xs text-red-600 dark:text-red-400 hover:underline"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <button
                    onClick={() => setForm({ ...form, lines: [...form.lines, blankLine()] })}
                    className="text-xs border dark:border-gray-600 dark:text-gray-200 rounded-lg px-2.5 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    + Add line
                  </button>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Subtotal <span className="font-mono">{money(draftSubtotal)}</span>
                    <span className="ml-2 text-[11px] text-gray-400 dark:text-gray-500">
                      tax added on save from the customer's rate
                    </span>
                  </div>
                </div>
              </div>

              <label className="block text-xs text-gray-500 dark:text-gray-400">
                Message to the customer
                <textarea
                  rows={2}
                  value={form.customer_message}
                  onChange={(e) => setForm({ ...form, customer_message: e.target.value })}
                  className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm dark:bg-gray-900 dark:text-gray-100"
                />
              </label>
              <label className="block text-xs text-gray-500 dark:text-gray-400">
                Terms (printed under the total)
                <textarea
                  rows={2}
                  value={form.terms}
                  onChange={(e) => setForm({ ...form, terms: e.target.value })}
                  className="mt-1 w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm dark:bg-gray-900 dark:text-gray-100"
                />
              </label>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => {
                  setCreating(false);
                  setOpen(null);
                }}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={busy === "save" || !form.title.trim()}
                className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy === "save" ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
